<template>
	<div class="body-block" :class="{ 'sidebar-xs': sidebarHide, 'sidebar-mobile-main': sidebarMobile }">
		<div
			class="navbar navbar-expand-md"
			:class="isClassic ? 'navbar-dark' : 'navbar-light navbar-clinical'"
		>
			<div v-if="isClassic" class="navbar navbar-brand py-0">
				<img class="navbar-classic-logo" src="/assets/images/devaresrehab-logo.svg" alt="DevaresRehab" />
			</div>
			<div v-else class="navbar topbar-logo navbar-brand">
				<img class="logo-mark" src="/assets/images/devaresrehab-icon.svg" alt="" />
				<div class="logo-text">
					<span class="logo-name">DevaresStationar</span>
					<span class="logo-system">Система управления лечением и проживанием</span>
				</div>
			</div>
			<div class="d-md-none">
				<button class="navbar-toggler sidebar-mobile-main-toggle" type="button" @click="onChangeSidebarMobile">
					<i class="icon icon-menu"></i>
				</button>
				<button class="navbar-toggler" type="button" @click="onChangeNavbarMobileShow">
					<i :class="{ 'icon icon-chevron-up': navbarMobileShow, 'icon icon-chevron-down': !navbarMobileShow }"></i>
				</button>
			</div>

			<div class="collapse navbar-collapse" :class="{ show: navbarMobileShow }">
				<ul class="navbar-nav">
					<li class="nav-item">
						<a href="#" class="navbar-nav-link sidebar-control sidebar-main-toggle d-none d-md-block" @click="onChangeSidebar">
							<i class="icon icon-menu"></i>
						</a>
					</li>
				</ul>

				<span class="status-chip status-chip--online" v-if="online">
					<span class="status-chip__dot"></span>
					Online
				</span>
				<span class="status-chip status-chip--offline" v-else>
					<span class="status-chip__dot"></span>
					Offline
				</span>

			<!-- УЗ / отделение (только просмотр — выбор на входе) -->
				<div
					v-if="orgTitle || activeDepartmentName"
					class="mx-2 navbar-org d-flex align-items-center"
				>
					<span v-if="orgTitle" class="navbar-org__name">{{ orgTitle }}</span>
					<span
						v-if="orgTitle && activeDepartmentName"
						class="navbar-org__sep"
					>·</span>
					<span v-if="activeDepartmentName" class="navbar-org__dept">{{ activeDepartmentName }}</span>
				</div>
				<div v-if="config.title" class="mx-2 navbar-org">
					<span>{{ config.title }}</span>
				</div>

				<ul class="navbar-nav ml-auto">
					<ThemeSwitcher />
					<Messages />
					<User />
				</ul>
			</div>
		</div>
		<!-- /main navbar -->

		<!-- Page content -->
		<div class="page-content">
			<!-- Main sidebar -->
			<Sidebar />
			<!-- /main sidebar -->

			<!-- Main content -->
			<div class="content-wrapper">
				<!-- Page header -->
				<div class="page-header page-header-light">
					<div class="page-header-content header-elements-md-inline" v-if="pageTitle">
						<div class="page-title d-flex">
							<h4>{{ pageTitle }}</h4>

							<a href="#" class="header-elements-toggle text-default d-md-none">
								<i class="icon-more"></i>
							</a>
						</div>
					</div>

					<BreadCrumb />
				</div>
				<!-- /page header -->

				<!-- Content area -->
				<slot />
				<!-- /content area -->

				<!-- Footer -->
				<Footer />
				<!-- /footer -->
			</div>
			<!-- /main content -->
		</div>
	</div>
</template>

<script>


import { computed, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';

import { sidebarHide, sidebarMobile, navbarMobileShow, pageTitle } from "./layouts";

import Sidebar from "./Sidebar";
import BreadCrumb from "./BreadCrumb";
import Messages from "./Dropdown/Messages";
import ThemeSwitcher from "./Dropdown/ThemeSwitcher";
import User from "./Dropdown/User";
import Footer from "./Footer";

import { availabeRole } from '@/helpers/access';

import { profile } from '@/api/users';
import { config } from '@/config';
import { getStoredTheme, onThemeChange } from '@/core/theme';

export default {
	name: "Default",

	components: {
		Sidebar, ThemeSwitcher, Messages, User, BreadCrumb, Footer
	},

	setup() {
		const { state, dispatch } = useStore();

		const theme = ref(getStoredTheme());
		const isClassic = computed(() => theme.value === 'classic');
		const stopThemeListen = onThemeChange((next) => {
			theme.value = next;
		});

		const userProfile = reactive({
			...JSON.parse(localStorage.getItem('userProfile')) || {}
		});

		const departmentLabel = ref('');

		const orgTitle = computed(() => {
			return (
				userProfile.employee?.organization ||
				state.user?._organization ||
				null
			);
		});

		const activeDepartmentName = computed(() => {
			const active = state.user?.activeDepartment
				? String(state.user.activeDepartment)
				: '';
			const options = Array.isArray(state.user?.departmentOptions)
				? state.user.departmentOptions
				: [];

			if (!active) {
				return 'Все отделения';
			}

			const found = options.find((d) => String(d.id || '') === active);
			if (found?.name) return found.name;

			// не подставляем «домашнее» отделение сотрудника — это другой id
			return departmentLabel.value || '';
		});

		const resolveDepartmentLabel = () => {
			const active = state.user?.activeDepartment
				? String(state.user.activeDepartment)
				: '';
			const options = Array.isArray(state.user?.departmentOptions)
				? state.user.departmentOptions
				: [];
			const found = options.find((d) => String(d.id || '') === active);
			if (found?.name) {
				departmentLabel.value = found.name;
				return;
			}
			if (!active) {
				departmentLabel.value = 'Все отделения';
				return;
			}
			departmentLabel.value = '';
		};

		onMounted(async () => {
			if (state.user.id) {
				const response = await profile(state.user.id);

				Object.assign(userProfile, response);
				localStorage.setItem('userProfile', JSON.stringify(userProfile));
				resolveDepartmentLabel();
			}
		});

		onUnmounted(() => {
			stopThemeListen();
		});

		return {
			sidebarHide,
			sidebarMobile,
			navbarMobileShow,
			pageTitle,
			config,
			state,
			user: computed(() => state.user),
			userProfile,
			orgTitle,
			activeDepartmentName,
			isClassic,
			onLogout: () => dispatch('logout'),
			availabeRole,

			onChangeSidebar: () => sidebarHide.value = !sidebarHide.value,
			onChangeSidebarMobile: () => sidebarMobile.value = !sidebarMobile.value,
			onChangeNavbarMobileShow: () => navbarMobileShow.value = !navbarMobileShow.value,
			online: computed(() => state.online)
		}
	}
}
</script>

<style scoped>
.text-semibold {
	font-weight: 600;
}

.topbar-logo {
	font-size: 20px;
	max-height: none;
	border: 0;
	display: inline-flex;
	align-items: center;
	gap: 0.65rem;
	padding: 0.35rem 1rem 0.35rem 1.1rem;
	margin-right: 0.25rem;
	line-height: inherit;
	white-space: nowrap;
}

.logo-mark {
	width: 2.15rem;
	height: 2.15rem;
	border-radius: 50%;
	object-fit: cover;
	background: #f7f1eb;
	flex-shrink: 0;
}

.logo-text {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	min-width: 0;
	gap: 0.12rem;
}

.logo-name {
	display: block;
	color: var(--dv-text);
	font-size: 1.05rem;
	font-weight: 700;
	line-height: 1.1;
	letter-spacing: -0.02em;
}

.logo-system {
	display: block;
	max-width: 16.5rem;
	color: var(--dv-text-muted);
	font-size: 0.6875rem;
	font-weight: 600;
	line-height: 1.25;
	letter-spacing: 0.01em;
	white-space: normal;
}

.navbar-org {
	gap: 0.45rem;
	flex-shrink: 1;
	min-width: 0;
	max-width: none;
	flex-wrap: wrap;
}

.navbar-org__name,
.navbar-org__dept {
	white-space: normal;
	overflow: visible;
	text-overflow: clip;
	line-height: 1.25;
	font-size: 0.8125rem;
	font-weight: 600;
}

.navbar-org__sep {
	color: var(--dv-text-muted);
	font-weight: 600;
}

@media (max-width: 767.98px) {
	.topbar-logo {
		flex: 1;
		min-width: 0;
		padding: 0.25rem 0.5rem 0.25rem 0;
		margin-right: 0.15rem;
	}

	.logo-name {
		font-size: 0.95rem;
	}

	.logo-system {
		max-width: 11rem;
		font-size: 0.58rem;
	}

	.navbar-org {
		max-width: 100%;
		margin-top: 0.35rem;
	}
}
</style>
